import React, { useEffect, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { Label, Typography } from '@packages/ui/shared';

/**
 * Default link URLs to https:// when the user omits a scheme. Without this, a
 * value like "google.com" is treated as a relative link and resolves against the
 * current app route (e.g. /administration/publication/google.com) instead of the
 * external site. Schemes we already understand (and in-page anchors) are left as
 * is. Patched once at module load since the Link blot is shared across editors.
 */
const LinkFormat = Quill.import('formats/link') as any;
const originalSanitize = LinkFormat.sanitize;
const hasSchemeOrAnchor = /^(https?:\/\/|mailto:|tel:|sms:|ftp:\/\/|#)/i;
LinkFormat.sanitize = (url: string) => {
    const normalized = url && !hasSchemeOrAnchor.test(url.trim()) ? `https://${url.trim()}` : url;
    return originalSanitize.call(LinkFormat, normalized);
};

/**
 * Basic-scope rich text editor for the publication Description.
 *
 * Toolbar is intentionally limited to Bold / Italic / Underline, ordered &
 * bullet lists and links. No image/source-view tools — that keeps the emitted
 * HTML small (it is Base64-encoded and sent through the WAF) and avoids the XSS
 * surface those tools introduce.
 *
 * Defined at module scope so the references stay stable across renders (passing
 * fresh `modules`/`formats` objects on every render makes Quill re-init).
 */
const modules = {
    toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
};

const formats = ['bold', 'italic', 'underline', 'list', 'link'];

interface Props {
    label?: string;
    value?: string | null;
    placeholder?: string;
    required?: boolean;
    errorMessage?: string;
    onChange: (html: string) => void;
}

const useStyles = createUseStyles({
    editor: {
        '& .ql-toolbar.ql-snow': {
            borderTopLeftRadius: 3,
            borderTopRightRadius: 3,
            borderColor: colors.grayFive,
        },
        '& .ql-container.ql-snow': {
            borderBottomLeftRadius: 3,
            borderBottomRightRadius: 3,
            borderColor: colors.grayFive,
            fontFamily: 'Roboto',
            fontSize: 17,
        },
        '& .ql-editor': {
            minHeight: 142,
            color: colors.textDarkPrimary,
        },
        '& .ql-editor.ql-blank::before': {
            color: colors.textDarkTertiary,
            fontStyle: 'normal',
        },
        '& .ql-snow .ql-tooltip': {
            zIndex: 30,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        },
    },
    invalid: {
        '& .ql-toolbar.ql-snow, & .ql-container.ql-snow': {
            borderColor: colors.redOne,
        },
    },
});

export const RichTextEditor = ({ label, value, placeholder, required, errorMessage, onChange }: Props) => {
    const classes = useStyles();
    // Hold the editor wrapper element so it can be passed to Quill as the popup
    // `bounds`. Quill defaults bounds to <body>, which lets the link popup clamp
    // to the page's left edge (under the sidebar); bounding it to the editor keeps
    // the popup next to the selection.
    const [boundsEl, setBoundsEl] = useState<HTMLDivElement | null>(null);

    // Quill toolbar buttons render as bare icons with no label. Add native
    // hover tooltips (e.g. so the `clean` eraser reads "Clear formatting").
    useEffect(() => {
        if (!boundsEl) {
            return;
        }
        const tooltips: Array<[string, string]> = [
            ['.ql-bold', 'Bold'],
            ['.ql-italic', 'Italic'],
            ['.ql-underline', 'Underline'],
            ['.ql-list[value="ordered"]', 'Numbered list'],
            ['.ql-list[value="bullet"]', 'Bulleted list'],
            ['.ql-link', 'Insert link'],
            ['.ql-clean', 'Clear formatting'],
        ];
        tooltips.forEach(([selector, title]) => {
            boundsEl.querySelector(selector)?.setAttribute('title', title);
        });
    }, [boundsEl]);

    return (
        <div>
            {!!label && <Label required={required}>{label}</Label>}

            <div ref={setBoundsEl} className={`${classes.editor} ${errorMessage ? classes.invalid : ''}`}>
                {boundsEl && (
                    <ReactQuill
                        theme="snow"
                        value={value ?? ''}
                        placeholder={placeholder}
                        onChange={onChange}
                        modules={modules}
                        formats={formats}
                        bounds={boundsEl}
                    />
                )}
            </div>

            {!!errorMessage && (
                <Typography variant="caption" color="redOne" style={{ marginTop: 8 }}>
                    {errorMessage}
                </Typography>
            )}
        </div>
    );
};
